import { useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { cx } from '@/shared/ui/cx';
import { useDismiss } from '@/shared/ui/useDismiss';

export type PopoverPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'top-start'
  | 'top-end'
  | 'right-start'
  | 'left-start';

export type PopoverProps = {
  open: boolean;
  onClose: () => void;
  /** The control the panel is positioned against; kept out of "outside" clicks. */
  anchorRef: RefObject<HTMLElement | null>;
  placement?: PopoverPlacement;
  className?: string;
  /** Names the panel for assistive tech. */
  label?: string;
  children: ReactNode;
};

/**
 * A floating panel anchored to a trigger.
 *
 * Positioning is CSS-only (the anchor is `position: relative`, the panel is
 * absolutely placed against it) rather than measured in JS. Every surface in
 * this design opens inside a container wide enough to hold it, so the collision
 * detection a full positioning library brings would be dead weight. If a panel
 * ever needs to flip against the viewport, that is the point to reach for one.
 */
export function Popover({
  open,
  onClose,
  anchorRef,
  placement = 'bottom-start',
  className,
  label,
  children,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useDismiss({ open, onDismiss: onClose, refs: [panelRef, anchorRef] });

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className={cx('popover', `popover--${placement}`, className)}
      role="dialog"
      aria-label={label}
      aria-modal={false}
    >
      {children}
    </div>
  );
}
